/**
 * The six report pages reached through the Reports submenu (`navModel.js`).
 * Each is: a breakdown/period control (via `ReportView.jsx`'s `ReportSelect`/
 * `ReportDateRange`), a chart built from whichever part of the envelope suits
 * it, a handful of `StatTile`s off `summary`, and `ReportView` for the rest
 * (table, Print, Download). See `reports.model.md` for the backend contract.
 */
import { useMemo, useState } from 'react';
import { BarChart, DonutChart, LineChart } from '../../components/charts/index.jsx';
import { money, number, percent } from '../../utils/formatters.js';
import { ReportView, ReportSelect, ReportDateRange } from './ReportView.jsx';
import {
  useLeadsReport, useOpportunitiesReport, useActivitiesReport, useRevenueReport, useForecastReport, useTerritoriesReport,
} from './useReports.js';

/** `''` from a date input means "not set" — only send what the caller actually picked. */
function useDateRange() {
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  return { from, to, setFrom, setTo, params: { ...(from ? { from } : {}), ...(to ? { to } : {}) } };
}

/* -------------------------------------------------------------------------- */
/* Leads                                                                       */
/* -------------------------------------------------------------------------- */

const LEAD_BREAKDOWN_OPTIONS = [
  { value: 'source', label: 'By source' },
  { value: 'owner', label: 'By salesperson' },
  { value: 'territory', label: 'By territory' },
  { value: 'status', label: 'By status' },
  { value: 'conversion', label: 'Conversion trend' },
  { value: 'aging', label: 'Aging (open leads)' },
];

export function LeadsReportPage() {
  const [breakdown, setBreakdown] = useState('source');
  const range = useDateRange();
  const { data, isLoading, error, refetch } = useLeadsReport({ breakdown, ...range.params });

  const chart = useMemo(() => {
    const rows = data?.rows ?? [];
    if (!rows.length) return null;
    if (breakdown === 'conversion') {
      return <LineChart data={rows} xKey="label" series={[{ key: 'captured', label: 'Captured' }, { key: 'converted', label: 'Converted' }]} height={260} />;
    }
    if (breakdown === 'aging') {
      return <BarChart data={rows} xKey="label" series={[{ key: 'count', label: 'Open leads' }]} height={260} />;
    }
    return <BarChart data={rows} xKey="label" series={[{ key: 'count', label: 'Leads' }, { key: 'converted', label: 'Converted' }]} height={260} />;
  }, [data, breakdown]);

  const s = data?.summary ?? {};
  const summaryTiles = [
    { key: 'total', label: 'Total leads', value: number(s.total_leads), tone: 'brand', icon: 'bi-magnet-fill' },
    { key: 'converted', label: 'Converted', value: number(s.converted), tone: 'emerald', icon: 'bi-check-circle-fill' },
    { key: 'rate', label: 'Conversion rate', value: percent(s.conversion_rate), tone: 'sky', icon: 'bi-arrow-repeat' },
    { key: 'score', label: 'Avg. score', value: number(s.avg_score), tone: 'amber', icon: 'bi-star-fill' },
  ];

  return (
    <ReportView
      title="Lead reports" subtitle="Volume, conversion and aging across every lead in scope."
      isLoading={isLoading} error={error} onRetry={refetch} data={data}
      summaryTiles={summaryTiles} chart={chart} filename="leads-report"
      controls={(
        <>
          <ReportDateRange from={range.from} to={range.to} onFrom={range.setFrom} onTo={range.setTo} />
          <ReportSelect label="Breakdown" value={breakdown} onChange={setBreakdown} options={LEAD_BREAKDOWN_OPTIONS} />
        </>
      )}
    />
  );
}

/* -------------------------------------------------------------------------- */
/* Opportunities                                                              */
/* -------------------------------------------------------------------------- */

const OPP_BREAKDOWN_OPTIONS = [
  { value: 'pipeline', label: 'Pipeline by stage' },
  { value: 'aging', label: 'Aging (time in stage)' },
  { value: 'win_loss', label: 'Win / loss analysis' },
  { value: 'cycle', label: 'Sales cycle by rep' },
];

export function OpportunitiesReportPage() {
  const [breakdown, setBreakdown] = useState('pipeline');
  const range = useDateRange();
  const { data, isLoading, error, refetch } = useOpportunitiesReport({ breakdown, ...range.params });

  const chart = useMemo(() => {
    const rows = data?.rows ?? [];
    if (!rows.length) return null;
    if (breakdown === 'cycle') {
      return <BarChart data={rows} xKey="label" series={[{ key: 'avg_cycle_days', label: 'Avg cycle (days)' }]} height={260} />;
    }
    return <BarChart data={rows} xKey="label" series={[{ key: 'value', label: 'Value' }]} money height={260} />;
  }, [data, breakdown]);

  const s = data?.summary ?? {};
  const summaryTiles = [
    { key: 'open', label: 'Open deals', value: number(s.total_open), tone: 'brand', icon: 'bi-briefcase-fill' },
    { key: 'value', label: 'Open value', value: money(s.open_value), tone: 'sky', icon: 'bi-cash-stack' },
    { key: 'winrate', label: 'Win rate', value: percent(s.win_rate), tone: 'emerald', icon: 'bi-trophy-fill' },
    { key: 'cycle', label: 'Avg. cycle', value: s.avg_cycle_days != null ? `${number(s.avg_cycle_days)}d` : '—', tone: 'amber', icon: 'bi-hourglass-split' },
  ];

  return (
    <ReportView
      title="Opportunity & pipeline reports" subtitle="Pipeline health, aging, and win/loss outcomes."
      isLoading={isLoading} error={error} onRetry={refetch} data={data}
      summaryTiles={summaryTiles} chart={chart} filename="opportunities-report"
      controls={(
        <>
          <ReportDateRange from={range.from} to={range.to} onFrom={range.setFrom} onTo={range.setTo} />
          <ReportSelect label="Breakdown" value={breakdown} onChange={setBreakdown} options={OPP_BREAKDOWN_OPTIONS} />
        </>
      )}
    />
  );
}

/* -------------------------------------------------------------------------- */
/* Activities                                                                 */
/* -------------------------------------------------------------------------- */

const ACTIVITY_BREAKDOWN_OPTIONS = [
  { value: 'by_kind', label: 'By kind' },
  { value: 'by_owner', label: 'By salesperson' },
];

export function ActivitiesReportPage() {
  const [breakdown, setBreakdown] = useState('by_kind');
  const range = useDateRange();
  const { data, isLoading, error, refetch } = useActivitiesReport({ breakdown, ...range.params });

  const chart = useMemo(() => {
    const rows = data?.rows ?? [];
    if (!rows.length) return null;
    if (breakdown === 'by_owner') {
      return <BarChart data={rows} xKey="label" series={[{ key: 'calls', label: 'Calls' }, { key: 'meetings', label: 'Meetings' }, { key: 'emails', label: 'Emails' }]} stacked height={260} />;
    }
    return (
      <DonutChart
        data={rows.map((r) => ({ label: r.label, value: r.count }))}
        centerLabel="Total" centerValue={number(rows.reduce((sum, r) => sum + r.count, 0))}
        height={240}
      />
    );
  }, [data, breakdown]);

  const s = data?.summary ?? {};
  const summaryTiles = [
    { key: 'total', label: 'Total activities', value: number(s.total_activities), tone: 'brand', icon: 'bi-list-check' },
    { key: 'completed', label: 'Completed', value: number(s.completed), tone: 'emerald', icon: 'bi-check2-circle' },
    { key: 'rate', label: 'Completion rate', value: percent(s.completion_rate), tone: 'sky', icon: 'bi-graph-up' },
    { key: 'overdue', label: 'Overdue', value: number(s.overdue), tone: 'rose', icon: 'bi-alarm-fill' },
  ];

  return (
    <ReportView
      title="Activity reports" subtitle="Calls, meetings, emails and follow-ups logged by the team."
      isLoading={isLoading} error={error} onRetry={refetch} data={data}
      summaryTiles={summaryTiles} chart={chart} filename="activities-report"
      controls={(
        <>
          <ReportDateRange from={range.from} to={range.to} onFrom={range.setFrom} onTo={range.setTo} />
          <ReportSelect label="Breakdown" value={breakdown} onChange={setBreakdown} options={ACTIVITY_BREAKDOWN_OPTIONS} />
        </>
      )}
    />
  );
}

/* -------------------------------------------------------------------------- */
/* Revenue                                                                    */
/* -------------------------------------------------------------------------- */

const REVENUE_BREAKDOWN_OPTIONS = [
  { value: 'owner', label: 'By salesperson' },
  { value: 'territory', label: 'By territory' },
  { value: 'product', label: 'By product' },
  { value: 'account', label: 'By customer' },
];
const PERIOD_OPTIONS = [
  { value: 'month', label: 'Monthly' }, { value: 'quarter', label: 'Quarterly' }, { value: 'year', label: 'Yearly' },
];

export function RevenueReportPage() {
  const [breakdown, setBreakdown] = useState('owner');
  const [period, setPeriod] = useState('month');
  const range = useDateRange();
  const { data, isLoading, error, refetch } = useRevenueReport({ breakdown, period, ...range.params });

  const trend = data?.trend ?? [];
  const chart = trend.length
    ? <LineChart data={trend} xKey="label" series={[{ key: 'value', label: 'Revenue' }]} money height={260} />
    : null;

  const s = data?.summary ?? {};
  const summaryTiles = [
    { key: 'total', label: 'Total revenue', value: money(s.total_revenue), tone: 'brand', icon: 'bi-cash-coin' },
    { key: 'deals', label: 'Deals won', value: number(s.deal_count), tone: 'emerald', icon: 'bi-trophy-fill' },
    { key: 'avg', label: 'Avg. deal size', value: s.avg_deal_size != null ? money(s.avg_deal_size) : '—', tone: 'sky', icon: 'bi-graph-up-arrow' },
  ];

  return (
    <ReportView
      title="Revenue reports" subtitle="Closed-won revenue by salesperson, territory, product or customer."
      isLoading={isLoading} error={error} onRetry={refetch} data={data}
      summaryTiles={summaryTiles} chart={chart} filename="revenue-report"
      controls={(
        <>
          <ReportDateRange from={range.from} to={range.to} onFrom={range.setFrom} onTo={range.setTo} />
          <ReportSelect label="Trend" value={period} onChange={setPeriod} options={PERIOD_OPTIONS} />
          <ReportSelect label="Breakdown" value={breakdown} onChange={setBreakdown} options={REVENUE_BREAKDOWN_OPTIONS} />
        </>
      )}
    />
  );
}

/* -------------------------------------------------------------------------- */
/* Forecast                                                                   */
/* -------------------------------------------------------------------------- */

export function ForecastReportPage() {
  const [period, setPeriod] = useState('quarter');
  const { data, isLoading, error, refetch } = useForecastReport({ period });

  const rows = data?.rows ?? [];
  const chart = rows.length
    ? <BarChart data={rows} xKey="label" series={[{ key: 'target', label: 'Target' }, { key: 'closed', label: 'Closed' }, { key: 'commit', label: 'Commit' }]} money height={260} />
    : null;

  const s = data?.summary ?? {};
  const summaryTiles = [
    { key: 'target', label: 'Total target', value: money(s.total_target), tone: 'brand', icon: 'bi-bullseye' },
    { key: 'closed', label: 'Total closed', value: money(s.total_closed), tone: 'emerald', icon: 'bi-cash-coin' },
    { key: 'attainment', label: 'Attainment', value: percent(s.attainment_pct), tone: 'sky', icon: 'bi-speedometer2' },
    { key: 'accuracy', label: 'Forecast accuracy', value: s.accuracy_pct != null ? percent(s.accuracy_pct) : '—', tone: 'amber', icon: 'bi-crosshair' },
  ];

  return (
    <ReportView
      title="Forecast reports" subtitle="Target vs. closed revenue, commit and best-case, by period."
      isLoading={isLoading} error={error} onRetry={refetch} data={data}
      summaryTiles={summaryTiles} chart={chart} filename="forecast-report"
      emptyMessage="No targets configured for this period yet."
      controls={<ReportSelect label="Period" value={period} onChange={setPeriod} options={PERIOD_OPTIONS} />}
    />
  );
}

/* -------------------------------------------------------------------------- */
/* Territories                                                                */
/* -------------------------------------------------------------------------- */

export function TerritoriesReportPage() {
  const range = useDateRange();
  const { data, isLoading, error, refetch } = useTerritoriesReport(range.params);

  const rows = data?.rows ?? [];
  const chart = rows.length
    ? <BarChart data={rows} xKey="label" series={[{ key: 'open_pipeline_value', label: 'Open pipeline' }, { key: 'won_value', label: 'Won revenue' }]} money height={260} />
    : null;

  const s = data?.summary ?? {};
  const summaryTiles = [
    { key: 'territories', label: 'Territories', value: number(s.total_territories), tone: 'brand', icon: 'bi-globe-americas' },
    { key: 'pipeline', label: 'Total open pipeline', value: money(s.total_pipeline), tone: 'sky', icon: 'bi-funnel-fill' },
    { key: 'won', label: 'Total won revenue', value: money(s.total_won), tone: 'emerald', icon: 'bi-trophy-fill' },
  ];

  return (
    <ReportView
      title="Territory performance" subtitle="Leads, pipeline and win rate, one row per territory."
      isLoading={isLoading} error={error} onRetry={refetch} data={data}
      summaryTiles={summaryTiles} chart={chart} filename="territories-report"
      controls={<ReportDateRange from={range.from} to={range.to} onFrom={range.setFrom} onTo={range.setTo} />}
    />
  );
}
